import {
  ModalHeader,
  ModalBody,
  ModalContent,
  ModalOverlay,
  VStack,
  Text,
  ModalCloseButton,
  Modal,
  HStack,
  Grid,
  GridItem,
  TableCaption,
  Table,
  Thead,
  Tr,
  Td,
  Tbody,
  ModalFooter,
  Button,
  Box,
  Spinner,
  Progress,
  Icon,
  Stack,
  Tag,
} from "@chakra-ui/react";
import * as yup from "yup";
import { useContext, useMemo, useRef, useState } from "react";
import { Select } from "chakra-react-select";
import { Controller, useForm } from "react-hook-form";
import ValidatedField from "./ValidatedField";
import WhiteBgInput from "./WhiteBgInput";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  createDialogContext,
  DialogProvider,
  useDialog,
} from "../../../../Common/Dialog";
import ReactS3Uploader from "react-s3-uploader";
import actions from "../../../../actions/index";
import { useLoading } from "../../../hooks/form";
import { FaCheckCircle, FaFileCsv } from "react-icons/fa";

export const ColumnMatchingModalContext = createDialogContext();

const optionToLabel = ({
  value,
  label,
}: {
  label?: string;
  value?: string;
}) => {
  if (value === "first_name") {
    return "First Name";
  }

  if (value === "name") {
    return "Full Name";
  }

  if (value === "last_name") {
    return "Last Name";
  }

  if (value === "notes") {
    return "Notes";
  }

  if (value === "tags") {
    return "Tags";
  }

  if (value === "quantity") {
    return "Quantity";
  }

  return label;
};

const ColumnMatchingModal = ({
  title,
  onSave,
  onClose,
  onGuestlistFileChange,
  guestlistColumns: {
    sample,
    file_key,
    schema_fields,
    saved_schema_fields = [],
  },
}: {
  guestlistColumns: {
    sample: any[];
    file_key: string;
    schema_fields: Array<{ is_match: boolean; column: string } | null>;
    saved_schema_fields: string[];
  };
  title: string;
  onGuestlistFileChange: (fileKey: string) => Promise<void>;
  onClose: () => void;
  onSave: (data: {
    file_key: string;
    schema_fields: string[];
  }) => Promise<void>;
}) => {
  const { isOpen, close: _close } = useContext(ColumnMatchingModalContext);
  const uploadRef = useRef<HTMLInputElement>(null);
  const dialog = useDialog();
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useLoading({
    csv: false,
  });
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const csvFile = files.find(
      (file) => file.type === "text/csv" || file.name.endsWith(".csv")
    );

    if (csvFile) {
      if (uploadRef.current) {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(csvFile);
        uploadRef.current.files = dataTransfer.files;

        // Trigger the change event manually
        const event = new Event("change", { bubbles: true });
        uploadRef.current.dispatchEvent(event);
      }
    }
  };

  const prefilled_schema = schema_fields.map((c) => {
    if (c?.is_match) {
      return {
        label: c.column,
        value: c.column,
      };
    }

    return null;
  });

  const didPrefillSchema = useMemo(() => {
    return prefilled_schema.some((c) => c !== null);
  }, [prefilled_schema]);

  const {
    control,
    reset,
    watch,
    register,
    setError,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm({
    mode: "onChange",
    criteriaMode: "all",
    reValidateMode: "onChange",
    resolver: yupResolver(
      yup.object({
        schema: yup.array().of(
          yup
            .object({
              label: yup.string().nullable(),
              value: yup.string(),
            })
            .nullable()
        ),
      })
    ),
    values: {
      schema: prefilled_schema,
    },
  });

  const close = () => {
    reset();
    _close();
    onClose();
  };

  const _onSubmit = (data: {
    name: string;
    schema: { label: string; value: string }[];
  }) => {
    if (
      !data.schema.some((c) => c?.value === "first_name" || c?.value === "name")
    ) {
      setError("schema", {
        message: "Name is required",
      });
      return;
    }
    return onSave({
      file_key,
      schema_fields: data.schema.map((c) => c?.value),
    }).finally(() => reset());
  };

  const fieldsUsed = watch("schema").map((c) => c?.value);
  const hasFirstName = watch("schema").some(
    (c) => c?.value === "first_name" || c?.value === "name"
  );

  const isTag = (cellIndex: number) => {
    return watch("schema")[cellIndex]?.value === "tags";
  };

  return (
    <Modal isOpen={isOpen} size="xl" onClose={close} isCentered>
      <ModalOverlay />
      <ModalContent
        mb={[0, "auto"]}
        minW={["full", "90%"]}
        borderBottomLeftRadius={[0, "md"]}
        borderBottomRightRadius={[0, "md"]}
      >
        <ModalHeader paddingTop={4}>
          <VStack gap={0} width={"full"}>
            <Text w="full">{title}</Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton onClick={close} />
        <ModalBody
          minH={["full", "500px"]}
          textAlign={"center"}
          w="full"
          px={4}
          display="flex"
        >
          <VStack w="full" spacing={8} mx={2}>
            <Text textAlign={"left"} w="full">
              The csv file should have a column with at least the first names of
              all guests. By default, all guests will be assigned a ticket
              quantity of 1 unless specified otherwise.
            </Text>

            {sample.length > 0 ? (
              <Stack w="full">
                <Text w="full" textAlign={"left"}>
                  Please assign the column headers to the content of the rows.
                  Columns that are not assigned will be ignored.
                </Text>
                {didPrefillSchema ? (
                  <HStack
                    rounded={"lg"}
                    p={4}
                    bg="green.100"
                    my={2}
                    spacing={2}
                  >
                    <Icon as={FaCheckCircle} color="green.500" />
                    <Text w="full" textAlign={"left"}>
                      We've automatically assigned{" "}
                      <Text as="span" fontWeight={"bold"}>
                        {prefilled_schema
                          .map((p) => p?.label)
                          .filter(Boolean)
                          .join(", ")}{" "}
                      </Text>
                      for you based on the content of the first row of the file.
                    </Text>
                  </HStack>
                ) : null}
              </Stack>
            ) : (
              <Box
                py={8}
                backgroundColor={isDragging ? "gray.300" : "gray.200"}
                rounded="lg"
                flex={1}
                w="full"
                display="flex"
                alignItems={"center"}
                justifyContent={"center"}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                cursor="pointer"
                transition="background-color 0.2s"
                _hover={{ backgroundColor: "gray.300" }}
                onClick={() => {
                  uploadRef.current?.click();
                }}
              >
                {loading.csv ? (
                  <VStack w="full">
                    <Spinner />
                    <Text>Uploading...</Text>
                    <Progress
                      size="md"
                      w={["full", "50%"]}
                      rounded={"lg"}
                      colorScheme={"green"}
                      mx={8}
                      value={progress}
                    />
                  </VStack>
                ) : (
                  <VStack spacing={8}>
                    <Icon fontSize={"3xl"} as={FaFileCsv} color="gray.400" />
                    <VStack>
                      <Text fontWeight={"bold"} textTransform={"uppercase"}>
                        Upload file
                      </Text>
                      <Text textAlign={"center"} color="gray.600">
                        {isDragging
                          ? "Drop your CSV file here"
                          : "Drop here the CSV file you want to import"}
                      </Text>
                    </VStack>
                  </VStack>
                )}
              </Box>
            )}
            {!hasFirstName && sample?.length > 0 ? (
              <Text w="full" textAlign={"left"} color="red.200" fontSize="sm">
                At least the first name is required
              </Text>
            ) : null}
            <Stack overflowX={"scroll"} position="relative" w="full">
              <Table w="full">
                <Thead w="full">
                  {sample &&
                    sample.at(0) &&
                    Object.keys(sample?.at(0))?.map((column, index) => (
                      <Td
                        key={column}
                        // width={`${100 / schema_fields.length}%`}
                        padding={1}
                        flexGrow={0}
                        flexShrink={0}
                      >
                        <ValidatedField
                          name="schema"
                          errors={errors}
                          as={Tr}
                          flexGrow={0}
                          flexShrink={0}
                        >
                          <Controller
                            key={column}
                            control={control}
                            rules={{
                              required: false,
                            }}
                            name={`schema.${index}`}
                            render={({ field }) => (
                              <Select
                                isDisabled={saved_schema_fields.length > 0}
                                chakraStyles={{
                                  container: () => ({
                                    position: "relative",
                                    flexGrow: 0,
                                    flexShrink: 0,
                                    width: "100%",
                                    minWidth: "200px",
                                  }),
                                }}
                                isClearable
                                {...field}
                                formatOptionLabel={(option) =>
                                  optionToLabel(option)
                                }
                                defaultValue={
                                  schema_fields[index]?.column
                                    ? {
                                        label:
                                          optionToLabel({
                                            value: schema_fields[index]?.column,
                                          }) || schema_fields[index]?.column,
                                        value: schema_fields[index]?.column,
                                      }
                                    : null
                                }
                                // @ts-ignore
                                options={schema_fields
                                  .filter(
                                    (c) => !fieldsUsed.includes(c?.column)
                                  )
                                  .filter((c) => c !== null)
                                  .map((c) => ({
                                    label:
                                      optionToLabel({
                                        value: c.column,
                                      }) || c.column,
                                    value: c.column,
                                  }))}
                                onChange={field.onChange}
                              />
                            )}
                          />
                        </ValidatedField>
                      </Td>
                    ))}
                </Thead>
                <Tbody w="full">
                  {sample.slice(0, 10).map((row, rowIndex) => (
                    <Tr key={rowIndex}>
                      {Object.values(row).map((r, cellIndex) => (
                        <Td
                          key={`${rowIndex}-${cellIndex}`}
                          // width={`${100 / schema_fields.length}%`}
                        >
                          {isTag(cellIndex) ? (
                            <HStack w="full" flexWrap={"wrap"} gap={1}>
                              {(r as string)
                                .split(",")
                                .filter(Boolean)
                                .map((t) => (
                                  <Tag
                                    overflow={"hidden"}
                                    flexGrow={0}
                                    flexShrink={0}
                                    key={t}
                                  >
                                    {t}
                                  </Tag>
                                ))}
                            </HStack>
                          ) : (
                            <Text>{r.toString()}</Text>
                          )}
                        </Td>
                      ))}
                    </Tr>
                  ))}
                  {sample.length > 10 && (
                    <Tr>
                      <Td colSpan={schema_fields.length}>
                        <Text color="gray.400" w="full" textAlign={"center"}>
                          Displaying a sample of {Math.min(10, sample.length)}{" "}
                          rows.
                        </Text>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Stack>
            <ReactS3Uploader
              style={{ display: "none" }}
              //@ts-ignore
              inputRef={uploadRef}
              getSignedUrl={(file, callback) =>
                actions.ADMIN.createUploadUrl(file, callback)
              }
              accept="text/csv"
              onProgress={(p) => {
                setProgress(p);
              }}
              preprocess={(f, next) => {
                setLoading("csv", true);
                next(f);
              }}
              onError={(e) => {
                dialog({ text: e });
              }}
              onFinish={(result) => {
                onGuestlistFileChange(result.fileKey).then(() => {
                  setProgress(0);
                  setLoading("csv", false);
                });

                if (uploadRef.current) {
                  uploadRef.current.value = "";
                }
              }}
              uploadRequestHeaders={(file) => ({
                "Content-Type": file.type,
                "x-amz-acl": "private",
              })}
              contentDisposition="auto"
            />
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack>
            <Button onClick={close}>Cancel</Button>
            {saved_schema_fields.length > 0 ? null : (
              <Button
                isDisabled={!hasFirstName}
                variant="kydDark"
                onClick={handleSubmit(_onSubmit)}
              >
                Import
              </Button>
            )}
            {errors.schema && (
              <Text color="red.200" fontSize="sm">
                {errors.schema.message}
              </Text>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const withColumnMatchingModal =
  <T extends object>(Component: React.ComponentType<T>) =>
  (props: T) => {
    return (
      <DialogProvider context={ColumnMatchingModalContext}>
        <Component {...(props as T)} />
      </DialogProvider>
    );
  };

export default ColumnMatchingModal;
